import React, { useEffect, useRef, type PropsWithChildren } from 'react';
import { FormattedMessage } from 'react-intl-next';

import { Text } from '@atlaskit/primitives/compiled';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import {
	PopoverProvider,
	PopoverTarget,
	SpotlightActions,
	SpotlightBody,
	SpotlightCard,
	SpotlightControls,
	SpotlightDismissControl,
	SpotlightFooter,
	SpotlightHeader,
	SpotlightHeadline,
	SpotlightPrimaryAction,
	SpotlightMedia,
	UNSAFE_UpdateOnChange as SpotlightUpdateOnChange,
} from '@atlaskit/spotlight';
import { cssMap } from '@atlaskit/css';
import { FadeIn } from '@atlaskit/motion';
import { token, useThemeObserver } from '@atlaskit/tokens';

import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets';

import { i18n } from './live-docs-spotlight-nudge-i18n';
import {
	useShareNudgeSpotlightStore,
	useShareNudgeSpotlightActions,
} from './store/useShareNudgeSpotlightStore';
import { useShareNudgeEligible } from './hooks/useShareNudgeEligible';

const styles = cssMap({
	root: {
		width: '100%',
	},
	wikiContentOverride: {
		font: token('font.heading.xsmall'),
	},
	video: {
		width: '100%',
		height: '100%',
	},
});

interface ShareNudgeSpotlightProps extends PropsWithChildren {
	onShareAction?: () => void;
}

export const ShareNudgeSpotlight = ({ children, onShareAction }: ShareNudgeSpotlightProps) => {
	const [{ shouldShowSpotlight }] = useShareNudgeSpotlightStore();
	const { setShouldShowSpotlight } = useShareNudgeSpotlightActions();
	const { PopoverContent } = useChoreographerAssets();
	const { hasSeenSpotlight } = useShareNudgeEligible();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { colorMode = 'light' } = useThemeObserver();
	const eventSentRef = useRef(false);

	const onShown = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'shown',
				actionSubject: 'spotlight',
				actionSubjectId: 'shareNudgeSpotlightShown',
				source: 'live-docs-spotlight-nudge',
			},
		}).fire();

		hasSeenSpotlight?.();
	};

	const handleDismiss = (dismissMethod?: 'manual' | 'auto') => {
		// Only send event if it hasn't been sent already; i.e if the user closes the spotlight manually and then the component unmounts
		if (!eventSentRef.current) {
			// Track dismissal analytics
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					source: 'live-docs-spotlight-nudge',
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'dismissedShareNudgeSpotlight',
					attributes: {
						dismissMethod,
					},
				},
			}).fire();

			eventSentRef.current = true;
		}

		setShouldShowSpotlight(false); // Persist dismissal in store
	};

	const handleShareAction = () => {
		// Track spotlight-specific analytics
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'live-docs-spotlight-nudge',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'shareNudgeSpotlightPrimaryAction',
			},
		}).fire();

		eventSentRef.current = true;

		// Call the share action callback from parent
		onShareAction?.();

		// Dismiss the spotlight
		handleDismiss();
	};

	// Call handleDismiss when component unmounts
	useEffect(() => {
		return () => {
			handleDismiss('auto');
		};
		// The ESLint warning about missing dependencies can be safely ignored here
		// since we specifically want the cleanup to capture the handleDismiss function from the initial render, not respond to its changes.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	if (!shouldShowSpotlight) {
		return <>{children}</>;
	}

	const videoSrcLinks = {
		light: 'https://dam-cdn.atl.orangelogic.com/AssetLink/675cqymci6vg4vkej46844jk484de7vt.mp4',
		dark: 'https://dam-cdn.atl.orangelogic.com/AssetLink/7xw0s4buw326783fjigafr060po81abn.mp4',
	};

	const posterLinks = {
		light: 'https://dam-cdn.atl.orangelogic.com/AssetLink/7pq28e418h47myd7dj6878413bhw0b1r.webp',
		dark: 'https://dam-cdn.atl.orangelogic.com/AssetLink/840eg7h41uq56m06lwh1ye72dp84h4xl.webp',
	};

	return (
		<div css={styles.root}>
			<PopoverProvider>
				<PopoverTarget>{children}</PopoverTarget>
				<SpotlightUpdateOnChange />
				<PopoverContent
					messageId="live-docs-share-nudge-spotlight"
					messageType="engagement"
					dismiss={() => handleDismiss('auto')}
					placement="bottom-start"
					isVisible={shouldShowSpotlight}
					onMessageDisposition={(status) => {
						if (status === 'started') {
							onShown();
						}
					}}
					shouldDismissOnClickOutside={false}
				>
					<FadeIn entranceDirection="right">
						{(props) => (
							<div {...props}>
								<SpotlightCard testId="live-docs-spotlight">
									<SpotlightHeader>
										<SpotlightHeadline>
											{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
											<span css={styles.wikiContentOverride}>
												<FormattedMessage {...i18n.makeTeamEffort} />
											</span>
										</SpotlightHeadline>
										<SpotlightControls>
											<SpotlightDismissControl onClick={() => handleDismiss('manual')} />
										</SpotlightControls>
									</SpotlightHeader>
									<SpotlightMedia>
										<video
											autoPlay
											playsInline
											loop
											muted
											poster={posterLinks[colorMode]}
											css={styles.video}
											src={videoSrcLinks[colorMode]}
										/>
									</SpotlightMedia>
									<SpotlightBody>
										<Text>
											<FormattedMessage {...i18n.shareDoc} />
										</Text>
									</SpotlightBody>
									<SpotlightFooter>
										<SpotlightActions>
											<SpotlightPrimaryAction onClick={handleShareAction}>
												<FormattedMessage {...i18n.letsGo} />
											</SpotlightPrimaryAction>
										</SpotlightActions>
									</SpotlightFooter>
								</SpotlightCard>
							</div>
						)}
					</FadeIn>
				</PopoverContent>
			</PopoverProvider>
		</div>
	);
};
